import type { CaptureResult } from "posthog-js";
import { redactPathTokens } from "../utils/pathNormalization";

/**
 * Keeping the mobile hand-off's credentials out of PostHog (PRD 87 A3).
 *
 * The post-verification redirect is
 * /subscribe?user_uuid=…&return_url=greenback://auth-callback?access_token=…&refresh_token=…
 * and that URL reaches PostHog in more places than $current_url: posthog-js
 * also sends the session's entry URL as $session_entry_url on EVERY event of
 * the session, and the person's first URL in $set_once ($initial_current_url
 * and friends). Listing the URL-bearing keys is how the leak survived the
 * first fix, so nothing here names one: every string the event carries is
 * checked.
 *
 * `return_url` is redacted whole: the tokens are inside its value
 * (`return_url=greenback%3A%2F%2Fauth-callback%3Faccess_token%3D…`), not
 * parameters of the page's own URL.
 *
 * `email` is not a credential but is personal data: a website link may carry
 * the visitor's address (/register?email=…), and PostHog records the page URL
 * on every pageview. The same checks keep it out.
 *
 * The public pages carry their credential in the PATH instead (/pay/:token,
 * /w9/:token, /t/:token…): the token is the access to the customer's invoice
 * or W-9. $current_url, $pathname and every other URL or path the event
 * carries has that segment replaced with `:token` (utils/pathNormalization).
 * Ids stay: they open nothing and help to read a funnel.
 */
export const SENSITIVE_URL_PARAMS: readonly string[] = ["access_token", "refresh_token", "token", "return_url", "email"];

export const REDACTED = "redacted";

const SENSITIVE_PARAM = new RegExp(`([?&#;]|^)(${SENSITIVE_URL_PARAMS.join("|")})=[^&#]*`, "gi");

const redactParams = (value: string): string =>
  value.replace(SENSITIVE_PARAM, (_match, separator: string, name: string) => `${separator}${name}=${REDACTED}`);

/** The value decoded one, two and three times, stopping when nothing changes. */
const decodings = (value: string): string[] => {
  const decoded: string[] = [];
  let current = value;
  for (let depth = 0; depth < 3; depth += 1) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      break;
    }
    if (next === current) break;
    decoded.push(next);
    current = next;
  }
  return decoded;
};

/**
 * `value` with every credential parameter's value replaced. A credential
 * still visible once the value is decoded (a return_url inside a return_to)
 * has no boundary the pattern can trust, so the whole value goes instead.
 */
export const redactCredentials = (value: string): string => {
  const redacted = redactParams(value);
  return decodings(redacted).some((decoded) => redactParams(decoded) !== decoded) ? REDACTED : redacted;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// Deep enough for any property posthog-js builds; a replay snapshot's DOM
// tree below this carries page content, not URLs.
const MAX_DEPTH = 10;

/** Every string in `value`, at any depth, through redactCredentials and redactPathTokens. Dates and other instances pass as they are. */
export const scrubCredentials = <T>(value: T, depth = 0): T => {
  if (typeof value === "string") return redactCredentials(redactPathTokens(value)) as unknown as T;
  if (depth >= MAX_DEPTH) return value;
  if (Array.isArray(value)) return value.map((item) => scrubCredentials(item, depth + 1)) as unknown as T;
  if (!isPlainObject(value)) return value;
  const scrubbed: Record<string, unknown> = {};
  Object.keys(value).forEach((key) => {
    scrubbed[key] = scrubCredentials(value[key], depth + 1);
  });
  return scrubbed as T;
};

/** posthog-js `before_send`: the last thing an event passes before it is sent. */
export const redactEventCredentials = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return event;
  return {
    ...event,
    properties: scrubCredentials(event.properties),
    ...(event.$set ? { $set: scrubCredentials(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: scrubCredentials(event.$set_once) } : {}),
  };
};
